import * as React from "react";
import { connect } from "react-redux";
import { AppDispatch } from "app/store";
import { injectIntl, WrappedComponentProps } from "react-intl";
// interfaces
import { IAppState } from "app/rootReducer";
import MeAPI from "@vingle/wtf-sdk/api/me";

// actions
import {
  changeUserInformation,
  confirmDeactivateAccount,
  confirmChangePassword,
  ActionCreators as ProfileActionCreators,
} from "@vingle/wtf-sdk/actions/userProfile";
import { pushToast } from "@vingle/wtf-sdk/actions/toast";
// components
import AccountSetting from "app/account/edit/components/accountSetting";
import ChangePasswordDialog from "app/account/edit/components/changePasswordDialog";
import DeactivateAccountDialog from "app/account/edit/components/deactivateAccountDialog";
import ConfirmModal, { CONFIRM_RESPONSE } from "common/components/confirmModal";
import { profileUserSelector } from "@vingle/wtf-sdk/selectors/userProfile";
// interfaces

interface IProps
  extends ReturnType<typeof mapStateToProps>,
    WrappedComponentProps {
  dispatch: AppDispatch;
}

function mapStateToProps(state: IAppState) {
  return {
    currentUser: state.app.currentUser,
    userProfile: {
      ...state.userProfile,
      profile: {
        ...state.userProfile.profile,
        profile: profileUserSelector(state),
      },
    },
  };
}

class EditProfile extends React.PureComponent<IProps> {
  private currentUserName: string = "";
  private checkDeactivateWithContent = false;

  public render() {
    const { currentUser, intl, userProfile } = this.props;

    if (!currentUser) {
      return null;
    }

    return (
      <>
        <AccountSetting
          currentUser={currentUser}
          isSaving={userProfile.meta.isProfileSaving}
          onSave={this.saveClick}
          onChangePasswordDialogOpen={this.passwordChangeDialogOpen}
          onDeactivateAccountDialogClick={this.deactivateAccountDialogOpen}
          onVerificationEmailResend={this.handleVerificationEmailResend}
        />
        <ChangePasswordDialog
          open={userProfile.meta.isOpenPasswordChangeDialog}
          isLoading={userProfile.meta.isLoadingForPasswordChange}
          initState={this.clearPasswordChangeDialog}
          doneHandler={this.passwordChangeDoneHandler}
          onRequestClose={this.passwordChangeDialogClose}
          failedToPasswordChange={Boolean(
            userProfile.meta.isFailedToPasswordChange,
          )}
        />
        <DeactivateAccountDialog
          isOpen={userProfile.meta.isOpenDeactivateAccountDialog}
          isLoading={userProfile.meta.isLoadingForDeactivating}
          onRequestClose={this.deactivateAccountDialogClose}
          deactivateHandler={this.showConfirmModal}
          failedReason={
            userProfile.meta.isFailedToDeactivateAccount !== null
              ? intl.formatMessage({ id: "user_edit.deactivate_failed" })
              : ""
          }
          usernameFailedOrNot={
            userProfile.meta.isFailedToDeactivateAccount
              ? userProfile.meta.isFailedToDeactivateAccount
              : false
          }
        />
        <ConfirmModal
          open={userProfile.meta.confirmModalState.open}
          message={this.props.intl.formatMessage({
            id: "user_edit.deactivate_confirm_message",
          })}
          submitButtonText={this.props.intl.formatMessage({
            id: "user_edit.deactivate_confirm_ok",
          })}
          onRequestClose={this.closeConfirmModal}
          onResponseCallback={this.confirmCallback}
        />
      </>
    );
  }

  private readonly saveClick = (params: {
    updateFields: Vingle.User.IUserForm;
    profile_image?: File | Blob | null;
  }) => {
    const { dispatch, currentUser } = this.props;
    if (currentUser) {
      dispatch(
        changeUserInformation({
          ...params,
          successMessageId: "user_edit.save_done",
        }),
      );
    }
  };

  private readonly passwordChangeDoneHandler = (
    currentUserName: string,
    newPassword: string,
  ) => {
    this.props.dispatch(
      confirmChangePassword(
        currentUserName,
        newPassword,
        this.props.intl.formatMessage({ id: "user_edit.password_change_done" }),
      ),
    );
  };

  private readonly passwordChangeDialogOpen = () => {
    this.props.dispatch(ProfileActionCreators.openPasswordChangeDialog());
  };

  private readonly passwordChangeDialogClose = () => {
    this.props.dispatch(ProfileActionCreators.closePasswordChangeDialog());
  };

  private readonly clearPasswordChangeDialog = () => {
    this.props.dispatch(ProfileActionCreators.clearPasswordChangeDialog());
  };

  private readonly deactivateAccountDialogOpen = () => {
    this.props.dispatch(ProfileActionCreators.openDeactivateAccountDialog());
  };

  private readonly deactivateAccountDialogClose = () => {
    this.props.dispatch(ProfileActionCreators.closeDeactivateAccountDialog());
  };

  private readonly showConfirmModal = (
    currentUserName: string,
    withContent: boolean,
  ) => {
    this.currentUserName = currentUserName;
    this.checkDeactivateWithContent = withContent;

    this.props.dispatch(ProfileActionCreators.openConfirmModal());
  };

  private readonly handleVerificationEmailResend = async () => {
    const { dispatch, intl } = this.props;
    try {
      await MeAPI.sendConfirmationMail();
      dispatch(
        pushToast({
          type: "SUCCESS",
          message: intl.formatMessage({ id: "confirm_dialog.success_to_send" }),
        }),
      );
    } catch (err) {
      dispatch(
        pushToast({
          type: "DANGER",
          message: intl.formatMessage({ id: "confirm_dialog.failed_to_send" }),
        }),
      );
    }
  };

  private readonly confirmCallback = (response: CONFIRM_RESPONSE) => {
    if (response === CONFIRM_RESPONSE.OK && this.currentUserName !== "") {
      this.props.dispatch(
        confirmDeactivateAccount(
          this.currentUserName,
          this.checkDeactivateWithContent,
        ),
      );
    }
  };

  private readonly closeConfirmModal = () => {
    this.props.dispatch(ProfileActionCreators.closeConfirmModal());
  };
}

export default connect(mapStateToProps)(injectIntl(EditProfile));
